import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getTVPlans, verifySmartcard, subscribeTv, getUserWallet } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";
import { icons } from "../../utils/icons";
import PageLoader from "../../components/PageLoader";
import BeneficiaryPicker from "../../components/feature/BeneficiaryPicker";
import { useBeneficiaries, saveBeneficiary } from "../../hooks/useBeneficiaries";
import CouponPicker    from "../../components/feature/CouponPicker";
import CashbackToggle      from "../../components/feature/CashbackToggle";
import PaymentConfirmSheet from "../../components/feature/PaymentConfirmSheet";
import MarketingSlides from "../../components/feature/MarketingSlides";
import { PhoneRequiredGate } from "../../components/feature/ProfilePromptBanner";
import { useAnalytics } from "../../hooks/useAnalytics";

// Provider accent — mapped onto the semantic token scale so each icon
// adapts correctly in dark mode instead of staying a fixed light-mode hex.
const PROVIDERS = [
  { id: "DSTV",      label: "DStv",      tone: "info",    img: icons.dstv },
  { id: "GOTV",      label: "GOtv",      tone: "danger",  img: icons.gotv },
  { id: "STARTIMES", label: "Startimes", tone: "warning", img: null       },
  { id: "SHOWMAX",   label: "Showmax",   tone: "purple",  img: null       },
];

const DEBOUNCE_MS   = 800;
const SMARTCARD_MIN = 10;

function formatDueDate(d) {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return d; }
}

export default function TVPage() {
  const navigate            = useNavigate();
  const { state: navState } = useLocation();
  const [searchParams]      = useSearchParams();
  const { user, userLoading = false, userReady }            = useAuth();
  const { mostRecent }      = useBeneficiaries("tv");
  const { track, conversion } = useAnalytics();

  // Priority: reuse (from success page) → mostRecent beneficiary → searchParams
  const prev = navState?.reuse || (mostRecent ? {
    provider:    mostRecent.provider,
    smartcardNo: mostRecent.smartcardNo,
  } : null);

  const [provider,     setProvider]     = useState(prev?.provider     || searchParams.get("provider") || "DSTV");
  const [smartcardNo,  setSmartcardNo]  = useState(prev?.smartcardNo  || "");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans,        setPlans]        = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [verifying,    setVerifying]    = useState(false);
  const [accountInfo,  setAccountInfo]  = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [pinError,     setPinError]     = useState("");
  const [nairaBalance, setNairaBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);

  const debounceTimer = useRef(null);
  const lastVerified  = useRef("");

  // ── Coupon + cashback state ────────────────────────────────
  const [appliedCoupon,  setAppliedCoupon]  = useState(null);
  const [cashbackToUse,  setCashbackToUse]  = useState(0);

  useEffect(() => {
    track.viewItem("tv");
  }, []);

  const grossAmount   = selectedPlan ? Number(selectedPlan.amount) : 0;
  const couponSaving  = appliedCoupon?.discount || 0;
  const totalSavings  = couponSaving + cashbackToUse;
  const netAmount     = Math.max(grossAmount - totalSavings, 0);

  const isValid    = smartcardNo.length >= SMARTCARD_MIN && !!selectedPlan && !!accountInfo;
  const hasBalance = nairaBalance !== null && selectedPlan ? nairaBalance >= netAmount : true;
  const canPay     = isValid && hasBalance;

  useEffect(() => {
    getUserWallet()
      .then(r => { if (r.status) setNairaBalance(r.data?.nairaBalance || 0); })
      .catch(() => {})
      .finally(() => setBalanceLoading(false));
  }, []);

  // Fetch plans when provider changes
  useEffect(() => {
    setPlansLoading(true);
    setSelectedPlan(null);
    setPlans([]);
    setAppliedCoupon(null);
    setCashbackToUse(0);
    getTVPlans(provider)
      .then(res => { if (res.status) setPlans(res.plans || []); })
      .catch(() => {})
      .finally(() => setPlansLoading(false));
  }, [provider]);

  // ── Core verify ───────────────────────────────────────────
  const runVerify = useCallback(async (p, s, planList) => {
    if (!p || s.length < SMARTCARD_MIN) return;
    const key = `${p}:${s}`;
    if (key === lastVerified.current) return;
    setVerifying(true);
    setAccountInfo(null);
    try {
      const res = await verifySmartcard(p, s);
      if (res.status) {
        setAccountInfo({
          customerName:   res.customerName,
          currentBouquet: res.currentBouquet,
          renewalAmount:  res.renewalAmount,
          dueDate:        res.dueDate,
          balance:        res.balance,
        });
        lastVerified.current = key;
        // Auto-select renewal plan
        if (res.renewalAmount && planList?.length) {
          const match = planList.find(pl => Number(pl.amount) === Number(res.renewalAmount));
          if (match) setSelectedPlan(match);
        }
      } else {
        toast.error(res.message || "Smartcard not found");
      }
    } catch (err) {
      const errData = err?.response?.data;
      const errCode = errData?.code || "";
      const errMsg  = errData?.message || "";

      if (errCode === "PIN_INVALID" || errCode === "PIN_LOCKED" || errCode === "PIN_REQUIRED") {
        setShowConfirm(true);
        setPinError(errMsg);
        setLoading(false);
        return;
      }

      if (err?.response?.status === 202 || errCode === "PROCESSING") {
        navigate("/bills/success", {
          state: {
            type:     "tv",
            title:    "TV Subscription",
            amount:   0,
            txStatus: "processing",
            reference: errData?.reference || null,
          },
        });
        return;
      }

      toast.error(errMsg || "TV Subscription failed");
    } finally {
      setVerifying(false);
    }
  }, []);

  // Debounced auto-verify on input change
  useEffect(() => {
    clearTimeout(debounceTimer.current);
    if (smartcardNo.length < SMARTCARD_MIN) { setAccountInfo(null); lastVerified.current = ""; return; }
    debounceTimer.current = setTimeout(() => runVerify(provider, smartcardNo, plans), DEBOUNCE_MS);
    return () => clearTimeout(debounceTimer.current);
  }, [provider, smartcardNo, plans, runVerify]);

  // Auto-verify immediately on mount if pre-filled (most recent beneficiary)
  // Wait for plans to load first so renewal plan can be auto-selected
  useEffect(() => {
    if (smartcardNo.length >= SMARTCARD_MIN && plans.length > 0) {
      runVerify(provider, smartcardNo, plans);
    }
  }, [plans]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Beneficiary selected ──────────────────────────────────
  const handleBeneficiarySelect = useCallback((b) => {
    if (b.provider && b.provider !== provider) {
      setProvider(b.provider);
      setSelectedPlan(null);
    }
    lastVerified.current = ""; // force re-verify
  }, [provider]);

  const handlePay = () => {
  if (canPay && !loading) {
    track.beginCheckout("tv", grossAmount); // fire before showing sheet
    setPinError("");
    setShowConfirm(true);
  }
};

  const executePay = async (pin) => {
    setShowConfirm(false);
    setLoading(true);
    try {
      const res = await subscribeTv(
        provider, smartcardNo, selectedPlan.variationCode,
        grossAmount,
        user?.phone,
        appliedCoupon?.couponId || null,
        appliedCoupon?.discount || 0,
        cashbackToUse,
        pin,
      );
      if (res.status) {
        saveBeneficiary({
          id:          smartcardNo,
          type:        "tv",
          smartcardNo,
          provider,
          label:       accountInfo?.customerName || smartcardNo,
          lastUsed:    Date.now(),
        });

        track.purchase("tv", netAmount, res.transactionId);
        // If API tells you it's first ever bill payment:
        if (res.isFirstBillPayment) {
          conversion.firstPurchase(netAmount, "tv");
        }
        navigate("/bills/success", {
          state: {
            type:          "tv",
            title:         `${provider} — ${selectedPlan.name}`,
            detail:        `Smartcard: ${smartcardNo}`,
            subDetail:     accountInfo?.customerName,
            amount:        netAmount,
            grossAmount, couponSaving, cashbackUsed: cashbackToUse,
            couponCode:    appliedCoupon?.code || null,
            requestId:     res.requestId,
            txStatus:      res.txStatus || "completed",
            transactionId: res.transactionId,
            // Bug fix: `variationCode` was referenced here without being
            // defined anywhere in this function's scope, which throws a
            // ReferenceError on every successful subscription. The plan's
            // variation code is selectedPlan.variationCode.
            reuse: { provider, smartcardNo, variationCode: selectedPlan.variationCode },
          },
        });
      } else {
        toast.error(res.message || "TV subscription failed");
      }
    } catch (err) {
      const errData = err?.response?.data;
      const errCode = errData?.code || "";
      const errMsg  = errData?.message || "";

      if (errCode === "PIN_INVALID" || errCode === "PIN_LOCKED" || errCode === "PIN_REQUIRED") {
        setShowConfirm(true);
        setPinError(errMsg);
        setLoading(false);
        return;
      }

      if (err?.response?.status === 202 || errCode === "PROCESSING") {
        navigate("/bills/success", {
          state: {
            type:     "tv",
            title:    "TV Subscription",
            amount:   0,
            txStatus: "processing",
            reference: errData?.reference || null,
          },
        });
        return;
      }

      toast.error(errMsg || "TV Subscription failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading || balanceLoading) return <PageLoader size="md" message="Loading…" />;

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Header */}
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/bills")} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
            <div>
              <h1 className="text-primary font-bold text-lg font-display">Cable TV</h1>
              <p className="text-muted text-xs">Subscribe or renew your cable</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-brand-light border border-brand-border px-3 py-1.5 rounded-xl">
            <i className="ri-wallet-3-line text-brand text-xs" />
            <span className="text-brand-text text-xs font-bold">
              {balanceLoading ? "…" : `₦${(nairaBalance || 0).toLocaleString()}`}
            </span>
          </div>
        </div>
      </div>

      <PhoneRequiredGate user={user} userLoading={userLoading} userReady={userReady}>
      <div className="flex-1 overflow-y-auto px-5 pt-5 pb-32 space-y-5">

        {/* Marketing slide */}
        <MarketingSlides page="tv" compact />

        {/* Provider */}
        <div>
          <p className="text-secondary text-sm font-semibold mb-3">Select Provider</p>
          <div className="grid grid-cols-4 gap-2">
            {PROVIDERS.map(p => (
              <button key={p.id}
                onClick={() => { setProvider(p.id); setAccountInfo(null); setSelectedPlan(null); lastVerified.current = ""; }}
                className={`flex flex-col items-center gap-2 py-3 rounded-2xl border-2 transition-all ${
                  provider === p.id ? "border-brand bg-brand-light" : "border-border bg-surface"
                }`}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-surface-muted">
                  {p.img
                    ? <img src={p.img} alt={p.label} className="w-7 h-7 object-contain" />
                    : <i className="ri-tv-2-line text-sm" style={{ color: `var(--color-${p.tone})` }} />}
                </div>
                <span className="text-[10px] font-bold text-secondary">{p.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Smartcard with BeneficiaryPicker */}
        <div>
          <p className="text-secondary text-sm font-semibold mb-3">Smartcard / IUC Number</p>
          <BeneficiaryPicker
            type="tv"
            value={smartcardNo}
            onChange={v => {
              setSmartcardNo(v);
              if (v.length < SMARTCARD_MIN) { setAccountInfo(null); lastVerified.current = ""; }
            }}
            onSelect={handleBeneficiarySelect}
            placeholder="Enter smartcard number"
            numeric
            maxLength={12}
            inputMode="numeric"
            suffix={
              verifying
                ? <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-warning)", borderTopColor: "transparent" }} />
                : accountInfo
                  ? <i className="ri-checkbox-circle-fill text-brand text-lg" />
                  : null
            }
          />

          {/* Account info card */}
          {accountInfo && (
            <div className="mt-2 px-4 py-3 bg-brand-light rounded-xl border border-brand-border space-y-2">
              <div className="flex items-center gap-2">
                <i className="ri-user-line text-brand text-sm" />
                <p className="text-brand-text text-sm font-bold">{accountInfo.customerName}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {accountInfo.currentBouquet && (
                  <div className="bg-surface rounded-lg px-3 py-2">
                    <p className="text-muted text-[10px]">Current Plan</p>
                    <p className="text-secondary text-xs font-bold truncate">{accountInfo.currentBouquet}</p>
                  </div>
                )}
                {accountInfo.renewalAmount && (
                  <div className="bg-surface rounded-lg px-3 py-2">
                    <p className="text-muted text-[10px]">Renewal Amount</p>
                    <p className="text-brand-text text-xs font-bold">₦{Number(accountInfo.renewalAmount).toLocaleString()}</p>
                  </div>
                )}
                {accountInfo.dueDate && (
                  <div className="col-span-2 bg-surface rounded-lg px-3 py-2">
                    <p className="text-muted text-[10px]">Due Date</p>
                    <p className="text-secondary text-xs font-bold">{formatDueDate(accountInfo.dueDate)}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Plans */}
        <div>
          <p className="text-secondary text-sm font-semibold mb-3">Choose Plan</p>
          {plansLoading ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-surface rounded-2xl border border-border animate-pulse" />)}
            </div>
          ) : plans.length === 0 ? (
            <div className="bg-surface rounded-2xl border border-border p-6 text-center">
              <p className="text-muted text-sm">No plans available for {provider}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {plans.map(plan => {
                const isRenewal = accountInfo?.renewalAmount && Number(plan.amount) === Number(accountInfo.renewalAmount);
                return (
                  <button key={plan.variationCode} onClick={() => setSelectedPlan(plan)}
                    className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-all relative shadow-card ${
                      selectedPlan?.variationCode === plan.variationCode
                        ? "border-brand bg-brand-light"
                        : "border-border bg-surface"
                    }`}>
                    {isRenewal && (
                      <span className="absolute top-0 right-0 text-[9px] font-bold px-2 py-0.5 rounded-bl-xl rounded-tr-xl bg-brand text-on-brand">
                        Renewal
                      </span>
                    )}
                    <div className="flex items-center gap-3 text-left">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        selectedPlan?.variationCode === plan.variationCode ? "border-brand" : "border-border-strong"
                      }`}>
                        {selectedPlan?.variationCode === plan.variationCode && (
                          <div className="w-2.5 h-2.5 bg-brand rounded-full" />
                        )}
                      </div>
                      <p className="font-semibold text-primary text-sm">{plan.name}</p>
                    </div>
                    <span className="font-bold text-brand text-sm flex-shrink-0">
                      ₦{Number(plan.amount).toLocaleString()}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Cashback toggle */}
        {selectedPlan && (
          <CashbackToggle
            purchaseAmount={grossAmount}
            appliedAmount={cashbackToUse}
            onApply={setCashbackToUse}
          />
        )}

        {/* Coupon picker */}
        {selectedPlan && (
          <div>
            <p className="text-secondary text-sm font-semibold mb-3">Coupon / Discount</p>
            <CouponPicker
              service="tv"
              amount={grossAmount}
              appliedCoupon={appliedCoupon}
              onApply={setAppliedCoupon}
              onRemove={() => setAppliedCoupon(null)}
            />
          </div>
        )}

        {/* Order summary */}
        {(appliedCoupon || cashbackToUse > 0) && selectedPlan && (
          <div className="bg-brand-light border border-brand-border rounded-2xl px-4 py-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-muted">{selectedPlan.name}</span>
              <span className="text-primary font-semibold">₦{grossAmount.toLocaleString()}</span>
            </div>
            {cashbackToUse > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-brand-text font-semibold flex items-center gap-1">
                  <i className="ri-hand-coin-line text-xs" />Cashback
                </span>
                <span className="text-brand-text font-bold">−₦{cashbackToUse.toLocaleString()}</span>
              </div>
            )}
            {appliedCoupon && (
              <div className="flex justify-between text-sm">
                <span className="text-brand-text font-semibold flex items-center gap-1">
                  <i className="ri-coupon-3-line text-xs" />{appliedCoupon.code}
                </span>
                <span className="text-brand-text font-bold">−₦{couponSaving.toLocaleString()}</span>
              </div>
            )}
            <div className="h-px bg-brand-border" />
            <div className="flex justify-between text-sm">
              <span className="text-primary font-bold">You pay</span>
              <span className="text-brand-text font-bold text-base">₦{netAmount.toLocaleString()}</span>
            </div>
          </div>
        )}
      </div>
      </PhoneRequiredGate>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-surface border-t border-border px-5 py-4 space-y-2">
        {selectedPlan && !hasBalance && nairaBalance !== null && (
          <div className="flex items-center gap-2 px-3 py-2 bg-danger-light border border-danger-border rounded-xl">
            <i className="ri-error-warning-line text-danger text-sm flex-shrink-0" />
            <p className="text-danger-text text-xs font-medium flex-1">
              Insufficient balance. <button onClick={() => navigate("/deposit")} className="underline font-semibold">Top up</button>
            </p>
          </div>
        )}
        {selectedPlan && (
          <div className="flex items-center justify-between px-3 py-2 bg-brand-light rounded-xl border border-brand-border">
            <span className="text-brand-text text-xs font-semibold">{selectedPlan.name}</span>
            <div className="text-right">
              {totalSavings > 0 && <p className="text-brand text-[10px] line-through">₦{grossAmount.toLocaleString()}</p>}
              <span className="text-brand-text text-xs font-bold">₦{(totalSavings > 0 ? netAmount : grossAmount).toLocaleString()}</span>
            </div>
          </div>
        )}
        <button onClick={handlePay} disabled={!canPay || loading}
          className="w-full bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2">
          {loading
            ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Processing…</>
            : verifying      ? "Verifying smartcard…"
            : !accountInfo   ? smartcardNo.length >= SMARTCARD_MIN ? "Verifying…" : "Enter smartcard number"
            : !selectedPlan  ? "Select a plan to continue"
            : !hasBalance    ? "Insufficient balance"
            : totalSavings > 0
              ? `Subscribe for ₦${netAmount.toLocaleString()} (saved ₦${totalSavings.toLocaleString()})`
              : `Subscribe for ₦${grossAmount.toLocaleString()}`}
        </button>
      </div>
      <PaymentConfirmSheet
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={executePay}
        loading={loading}
        pinError={pinError}
        amount={grossAmount}
        netAmount={netAmount}
        couponSaving={couponSaving}
        cashbackUsed={cashbackToUse}
        rows={[
          { label: "Provider",      value: provider },
          { label: "Smartcard No.", value: smartcardNo, mono: true },
          { label: "Account Name",  value: accountInfo?.customerName || "—" },
          { label: "Plan",          value: selectedPlan?.name || "" },
        ]}
        confirmLabel="Subscribe"
      />
    </div>
  );
}
